/**
 * The manual, kept beside the screens it describes — TECHNICAL_DESIGN.md §4.
 *
 * Written here rather than shipped as a PDF for the same reason the bundle
 * carries its own assets: §2's appliance may sit on a network with no route
 * out, so *"see the documentation on our website"* is a sentence nobody can act
 * on. Help that travels with the build is also help that cannot describe a
 * version the customer is not running.
 *
 * Each entry answers the four things somebody actually asks when a screen is
 * new to them: what it is for, what has to exist before it works, what to do,
 * and who is allowed to. **Roles are stated on every screen** because this
 * product's roles are not a ladder — an administrator cannot approve a limit and
 * an approver cannot administer the appliance — so *"you don't have permission"*
 * is a sentence people meet legitimately and should be able to understand.
 *
 * The one thing every page keeps in view is **where a limit goes next.** The base
 * product exports rules into the firm's OMS or compliance engine, where pre-trade
 * checks run; the Snapshot Evaluator add-on pack evaluates them against holdings
 * inside GUARD. Copy here describes what each gives, and never reads as a
 * limitation (IMPLEMENTATION.md decision 27).
 */

export interface HelpTopic {
  /** Matches the tab id, so the current screen's help is one lookup away. */
  id: string
  title: string
  purpose: string
  before?: string[]
  steps?: { what: string; detail: string }[]
  workflow?: string
  roles: string
  notes?: string[]
  /** Carries the matrix of roles against what each may do, below *Who can do what*. */
  roleMatrix?: boolean
  /** An add-on pack the topic belongs to. Left out of the manual on an installation
   *  that does not run it, as its screens are left out of the menus. */
  pack?: string
}

export const ROLE_COLUMNS = ['viewer', 'author', 'approver', 'admin'] as const
export type RoleName = (typeof ROLE_COLUMNS)[number]

/**
 * What each role may do, one row per capability — §10.1.
 *
 * Written out rather than fetched, because the manual is read where there is no
 * session to ask with. It cannot drift from the role model: `tests/test_role_matrix.py`
 * reads this list and holds every row to `authz.CAPABILITIES`, and fails when a
 * capability is added without a row. One row per line, in this shape, for that test.
 */
export const ROLE_MATRIX: { task: string; capability: string; roles: RoleName[]; pack?: string }[] = [
  { task: 'Read limits, their history and where they came from', capability: 'guideline.read', roles: ['viewer', 'author', 'approver', 'admin'] },
  { task: 'Browse the Dictionary and the mandate documents', capability: 'dictionary.browse', roles: ['viewer', 'author', 'approver', 'admin'] },
  { task: 'Read holdings snapshots and verdicts (Snapshot Evaluator)', capability: 'evaluation.run', roles: ['viewer', 'author', 'approver', 'admin'], pack: 'snapshot_evaluator' },
  { task: 'Draft and propose limits', capability: 'rule.draft', roles: ['author', 'approver'] },
  { task: 'Register mandate documents', capability: 'document.register', roles: ['author', 'approver'] },
  { task: 'Confirm a limit is still right when it comes up for review', capability: 'rule.review.confirm', roles: ['author', 'approver'] },
  { task: 'Sign off a fund', capability: 'portfolio.attest', roles: ['author', 'approver'] },
  { task: 'Deal with refused mailbox items', capability: 'submission.handle', roles: ['author', 'approver'] },
  { task: 'Propose a limit for a whole group', capability: 'scope.attach_rule', roles: ['author', 'approver'] },
  { task: 'Load holdings snapshots and benchmark figures (Snapshot Evaluator)', capability: 'holdings.load', roles: ['author', 'approver'], pack: 'snapshot_evaluator' },
  { task: 'Record a sale that cured a downgrade, or open a cure obligation (Snapshot Evaluator)', capability: 'cure.record', roles: ['author', 'approver'], pack: 'snapshot_evaluator' },
  { task: 'Put proposed limits into force', capability: 'rule.approve', roles: ['approver'] },
  { task: 'Decline proposals', capability: 'rule.reject', roles: ['approver'] },
  { task: 'Change the Dictionary', capability: 'dictionary.edit', roles: ['approver'] },
  { task: 'Answer a change to a mandate document', capability: 'document.drift.acknowledge', roles: ['approver'] },
  { task: 'Export limits for your OMS', capability: 'rule.export', roles: ['approver'] },
  { task: 'Export the audit pack', capability: 'audit.pack.export', roles: ['approver'] },
  { task: 'Waive a cure obligation on the client’s consent (Snapshot Evaluator)', capability: 'cure.waive', roles: ['approver'], pack: 'snapshot_evaluator' },
  { task: 'Set settlement calendars and escalation (Snapshot Evaluator)', capability: 'cure.configure', roles: ['approver'], pack: 'snapshot_evaluator' },
  { task: 'Review verdicts for compliance: agree or override, with a note (Snapshot Evaluator)', capability: 'verdict.review', roles: ['approver'], pack: 'snapshot_evaluator' },  { task: 'Read the audit trail', capability: 'audit.trail.read', roles: ['approver', 'admin'] },
  { task: 'Add funds and groups', capability: 'scope.create', roles: ['admin'] },
  { task: 'Put funds into groups', capability: 'scope.membership', roles: ['admin'] },
  { task: 'Set up desks, and choose which desk runs each fund', capability: 'desk.manage', roles: ['admin'] },
  { task: 'Confirm how a client’s documents are laid out', capability: 'layout_profile.manage', roles: ['admin'] },
  { task: 'Set up the guideline mailbox', capability: 'connector.configure', roles: ['admin'] },
  { task: 'Import guideline packs', capability: 'pack.import', roles: ['admin'] },
  { task: 'Manage accounts and roles', capability: 'user.manage', roles: ['admin'] },
  { task: 'Run the system: health, records and support', capability: 'appliance.operate', roles: ['admin'] },
]

export const HELP: HelpTopic[] = [
  {
    id: 'lookup',
    title: 'Lookup',
    purpose:
      'Everything that binds one portfolio, in one place: the limits written against it and the limits it inherits from a scope it belongs to.',
    before: ['A portfolio exists.', 'Rules have been approved against it, or against a scope it is in.'],
    steps: [
      {
        what: 'Choose a portfolio',
        detail:
          'The list is every portfolio you may see. The guidelines below are resolved as of now.',
      },
      {
        what: 'Read the binding rule',
        detail:
          'Where two rules cover the same thing, the more restrictive one binds and is marked. The other is shown struck through rather than hidden, because "why is this the limit" is answered by seeing what it beat.',
      },
      {
        what: 'Follow a rule to its source',
        detail:
          'Each limit shows the document and clause it came from. That is the point of it: a limit nobody can trace back is one that eventually gets argued about.',
      },
      {
        what: 'Retire a limit the client has dropped',
        detail:
          'On the limit’s ⋯ menu, Propose to retire, with the day it no longer applies from and why. It stays in force until an approver who did not propose it agrees on Approvals. New and changed limits come in by importing the client’s file, by hand or by email.',
      },
    ],
    workflow:
      'The main screen. Everything else here exists to make what appears on it correct.',
    roles: 'Anyone signed in. Reading a limit is not a privileged act — it is the job.',
    notes: [
      'Export these limits to your OMS for pre-trade checks. With the Snapshot Evaluator pack, GUARD evaluates your holdings against them as well.',
      'A rule marked inherited is not written against this portfolio; it comes from a scope the portfolio belongs to, and changing it changes it for every portfolio in that scope.',
    ],
  },
  {
    id: 'compare',
    title: 'Compare',
    purpose:
      'Two portfolios side by side, so a difference between mandates is visible rather than remembered.',
    before: ['At least two portfolios with rules against them.'],
    steps: [
      { what: 'Pick both portfolios', detail: 'Either order; the comparison is symmetric.' },
      {
        what: 'Read down the shared rows',
        detail:
          'A row where the two differ is the interesting one — a limit one fund has and the other does not, or the same limit at two thresholds.',
      },
    ],
    workflow:
      'Used when onboarding a new fund against an existing one, and when a client asks why two of their mandates behave differently.',
    roles: 'Anyone signed in.',
  },
  {
    id: 'search',
    title: 'Search',
    purpose:
      'Find a rule across every portfolio by what it says — a measure, a clause reference, a phrase from the mandate.',
    before: ['Rules exist. Proposed rules are excluded unless you ask for them.'],
    steps: [
      { what: 'Type what you remember', detail: 'Measure codes, clause references and narrative text are all searched.' },
      {
        what: 'Narrow by portfolio if you need to',
        detail: 'Without a portfolio the search is firm-wide, which is usually what you want when a regulator asks whether anything anywhere says something.',
      },
    ],
    workflow: 'The question this answers is "does anything in the firm say X".',
    roles: 'Anyone signed in.',
  },
  {
    id: 'migration',
    // The menu's own words, so the help is found under the name the screen is
    // opened by. It was titled *Migrate*, the project's name for the job, and later
    // *Import a workbook*, before the menu itself was renamed.
    title: 'Manually upload guidelines',
    purpose:
      'Load an existing guideline spreadsheet, with the numbers checked rather than taken on trust.',
    before: [
      'The workbook itself, as a spreadsheet.',
      'Vocabulary the workbook uses — see Dictionary, and the starter market profiles.',
      'A second operator, because the numbers are keyed twice.',
    ],
    steps: [
      {
        what: 'Upload the workbook',
        detail:
          'The shape is detected and matched against any layout profile already saved for it. An unfamiliar shape is mapped once, by a person, and read the same way for ever after.',
      },
      {
        what: 'Say what each row means',
        detail:
          'The system reads your row labels against the terms you have defined and suggests, with the reason beside each, the measure a row counts and which holdings it applies to: corporates taken together, each issuer among corporates, each country. A minimum rating becomes a floor under every holding or under the fund’s average, and the split-rating and unrated rows say how a rating is read. Check them and change any you disagree with. Anything left unmapped still imports, as text.',
      },
      {
        what: 'Parse',
        detail:
          'Each row becomes a draft limit carrying a reference back to the cell it came from, so a reviewer can go and look at it.',
      },
      {
        what: 'Reconcile the second keying',
        detail:
          'Paste the second operator’s sheet. Rows that disagree are shown as divergences; a row one operator skipped is a divergence too, not a silent pass.',
      },
      {
        what: 'Stage, then sign off per portfolio',
        detail:
          'Candidates become proposed rules. Nothing is in force until an approver approves it on the Approvals screen.',
      },
    ],
    workflow:
      'Usually the first thing you do. What comes out is a set of proposed limits — the same as anything arriving by mail — and an approver decides on them.',
    roles:
      'An author drafts and reconciles. An approver signs off per portfolio. An administrator manages layout profiles but cannot approve.',
    notes: [
      'A draft with no clause reference can be staged but not approved. A proposal may be incomplete; a limit in force may not.',
      'The suggested mappings are matched against the terms in your own dictionary. Nothing is inferred beyond that, nothing leaves the appliance, and a row it is unsure about is left for you rather than guessed at.',
      'A share of NAV says which holdings it covers. A row still covering the whole fund is named when the workbook is read, before anything reaches an approver, because the whole fund is always all of itself and no compliance system can check that.',
    ],
  },
  {
    id: 'documents',
    title: 'Mandate documents',
    purpose:
      'The documents your limits come from — the agreement, side letters, the client’s own guideline pack. A limit has to name one before it can be signed off.',
    before: ['A link somebody inside your network can open — the file itself stays in your document system.'],
    steps: [
      { what: 'Add the document', detail: 'Its title, what sort of document it is, and where it lives. Nothing is uploaded or copied.' },
      {
        what: 'Cite it from Clause review',
        detail: 'Once a document is here it is offered on Clause review, where each limit is matched to the clause it came from.',
      },
      {
        what: 'Watch for “Link broken”',
        detail:
          'The nightly check could not open the document where it used to be, or found different content. The limits citing it still stand, but somebody should say whether they still should.',
      },
    ],
    workflow:
      'Do this before Clause review, under Approvals. There is nothing to check limits against until at least one document is here.',
    roles: 'An author adds documents. Anyone signed in can see the list.',
  },
  {
    id: 'clauses',
    title: 'Clause review',
    purpose:
      'Read the mandate document itself, clause by clause, and record which clauses became rules and which did not.',
    before: ['The document added on Mandate documents.', 'Limits drafted from it, usually by importing a workbook.'],
    steps: [
      {
        what: 'Work down the limits waiting for a document',
        detail:
          'For each, either the document confirms it — pick the document, type the clause — or it says otherwise, which raises a finding.',
      },
      {
        what: 'Record a finding where a clause has no rule',
        detail:
          'That is the gap an auditor asks about. A finding is a statement that somebody looked and decided, which silence is not.',
      },
    ],
    workflow:
      'Between importing and approving, which is why it sits under Approvals. Import turns a workbook into limits; this checks them against the contract the workbook was meant to reflect, and nothing gets approved until it has.',
    roles: 'An author records findings. Reading is open to anyone signed in.',
  },
  {
    id: 'approvals',
    title: 'Approvals',
    purpose:
      'Every proposed rule waiting for a decision, wherever it came from — a migration, the mailbox, or somebody drafting in the portal — and every limit proposed for retirement.',
    before: [
      'A proposal exists.',
      'It cites a document and clause, or approval will refuse it.',
      'Somebody other than the person who proposed it — nobody approves their own work.',
    ],
    steps: [
      {
        what: 'Read the proposal against its source',
        detail:
          'Each carries the document, the clause and — for a mailbox submission — the cell it was read from.',
      },
      {
        what: 'Check which funds it will affect',
        detail:
          'A limit set on a group applies to every fund in it, and you see that list before you approve. If the group changes while you are reading, the approval stops and shows you the new list — it is never applied to a set of funds you did not see.',
      },
      {
        what: 'Decide a retirement',
        detail:
          'Limits proposed for retirement are listed first, with the day they would stop applying, why, and the funds they reach. Approving ends the limit from that day; it stays the right answer for every day it applied. Declining leaves it in force.',
      },
      { what: 'Approve, or decline with a reason', detail: 'The reason is recorded and is what the requester reads.' },
    ],
    workflow:
      'The single gate. Nothing in this product puts a limit into force except an approver here.',
    roles:
      'An approver decides. Everyone else can see what is pending — including the author whose client is waiting.',
  },
  {
    id: 'reviews',
    title: 'Reviews',
    purpose:
      'Rules whose review date has come round, and documents that have changed since a rule cited them.',
    before: ['Rules with a review interval — set at migration, per rule.'],
    steps: [
      { what: 'Take your own queue first', detail: 'The default view is what is yours to confirm.' },
      {
        what: 'Confirm the rule still reflects its document',
        detail: 'Confirming records who checked and when; it does not change the rule.',
      },
      {
        what: 'Answer a drift finding',
        detail:
          'A cited document whose content has changed raises one finding, not one per night. Acknowledging it re-baselines the document, which is the moment somebody asserts the citing rules are still correct despite the change — acknowledging without re-baselining is supported, for a reviewer who cannot yet make that assertion.',
      },
    ],
    workflow:
      'This is what a spreadsheet never had. The old workbook went out of date because nothing ever made anybody check it.',
    roles: 'An author or an approver confirms. An administrator cannot: confirming that a limit is still right is not an administrative task.',
  },
  {
    id: 'attest',
    title: 'Attest',
    purpose:
      'A periodic signed statement that a portfolio’s rule set is accurate and complete against its mandate.',
    before: [
      'The portfolio’s rules are current — overdue reviews block an attestation, and are listed with what is blocking.',
    ],
    steps: [
      { what: 'Clear the blockers', detail: 'A rule overdue for review must be confirmed first. A blocker on an inherited firm policy is not yours to clear; its owner reviews it.' },
      {
        what: 'Affirm accuracy and completeness',
        detail:
          'Both, separately, because they are different claims: that what is recorded is right, and that nothing is missing.',
      },
    ],
    workflow:
      'The periodic sign-off an auditor asks for. Afterwards this system can show exactly which limits applied to the fund at the moment you signed.',
    roles: 'An author or an approver signs off. An administrator does not — a sign-off from somebody who does not own the limits leaves nobody accountable for it.',
  },
  {
    id: 'export',
    title: 'Export',
    purpose:
      'The limits as a file, for the system that checks portfolios against them. This is what everything else here is for.',
    before: [
      'Limits approved and in force. Anything still awaiting approval is not included.',
      'A decision about which engine receives it and in which format.',
    ],
    steps: [
      {
        what: 'Choose the scope and the format',
        detail:
          'Every fund, or one. Use JSON unless your system needs CSV — both hold the same limits, but CSV squeezes the more detailed fields into single cells.',
      },
      {
        what: 'Record the digest with the file',
        detail:
          'It is how the file can later be confirmed as the one this system produced, unaltered.',
      },
      {
        what: 'Check what would be exported',
        detail:
          'Compares the file against the limits recorded here, including the ones a fund inherits from a group. Worth doing before you hand the file over.',
      },
      {
        what: 'Check a file you already have',
        detail:
          'Upload a file you exported earlier and this confirms it is still exactly what this system produced — useful after it has passed through another system.',
      },
    ],
    workflow:
      'The last step. Your own system enforces the file from here on.',
    roles:
      'An approver exports. Not a viewer and not an administrator: sending the firm’s limits to another system is a decision, not a read.',
    notes: [
      'Nothing is sent anywhere. The file downloads to you and you load it into your own system, so there is no outbound connection for your network team to approve.',
      'Some limits are written in words rather than as a number — a restriction on a named issuer, say. They are exported as text and marked as such, so your compliance team applies them alongside the rules your system executes.',
      'Your OMS runs pre-trade checks against this file. The Snapshot Evaluator add-on pack runs the same rules against your holdings inside GUARD.',
    ],
  },
  {
    id: 'export-verify',
    title: 'Check a file',
    purpose:
      'Confirm that a file you exported earlier is still exactly what this system produced.',
    before: ['A file you exported before.'],
    steps: [
      {
        what: 'Upload it',
        detail:
          'Every limit in the file is read back and compared with what is recorded here, and the reference number is checked.',
      },
    ],
    workflow:
      'After your integration has picked the file up, or before you rely on an older copy of one.',
    roles: 'An approver, the same as exporting.',
    notes: [
      'This is the check that catches a file altered on the way. The check on the export screen compares the limits themselves and cannot see a change made to the file afterwards.',
    ],
  },
  {
    id: 'export-pack',
    title: 'Audit pack',
    purpose:
      'One file for an auditor or a client: a fund’s limits, who signed off on them and when, every change with the person who made it, and anything overdue for review.',
    before: ['Limits in force, and ideally a sign-off or two behind them.'],
    steps: [
      { what: 'Choose the fund', detail: 'Or leave it empty for every fund.' },
      {
        what: 'Produce the pack',
        detail:
          'It lists what it will contain before producing anything, and the file downloads to you.',
      },
    ],
    workflow: 'When somebody outside the firm asks how you know what your limits are.',
    roles:
      'An approver. It gathers a whole fund’s limits and their history into one file for somebody outside the firm, which is a disclosure rather than a read.',
  },
  {
    id: 'dictionary',
    title: 'Dictionary',
    purpose:
      'The vocabulary rules are written in — measures, bases, classification schemes and rating ladders.',
    before: ['Nothing. This is usually the first screen a new appliance needs.'],
    steps: [
      {
        what: 'Start from a market',
        detail:
          'Starter markets adds the measures and bases a mandate of that kind is normally written in. Every market also brings the terms all markets share: countries, currencies, regions, asset classes, the agencies’ long-term rating scales, measures such as share of NAV and tracking error, and the column names holdings files use. It never overwrites: anything you have already defined is left exactly as it is.',
      },
      {
        what: 'Read what a term means',
        detail: 'Defined terms is the reference. If two people mean different things by one word, this is where that gets settled.',
      },
      {
        what: 'Ask for a term you need',
        detail:
          'Requests. An author asks; an approver admits. Where the request is close to an existing term, the approver has to say so before it goes in — `duration` and `duration_years` are not reconcilable once a rule cites each.',
      },
    ],
    workflow:
      'Before limits. A limit can only use terms defined here, which is what stops two desks meaning different things by the same word.',
    roles:
      'Anyone signed in can read them. An author can ask for a new term; an approver lets it in, and is the only role that can change what an existing term means. An approver or an administrator can load a starter market.',
    notes: [
      'Industry classifications such as GICS and ICB start empty, so you enter exactly the codes your own mandates refer to, under your own licence for those lists.',
    ],
  },
  {
    id: 'groups',
    title: 'Fund groups',
    purpose:
      'A set of funds that share limits — an asset class, a regulatory regime, a firm-wide policy. A limit attached to the group applies to every fund in it.',
    before: ['Nothing. A group can exist before any fund is in it.'],
    steps: [
      {
        what: 'Add the group',
        detail:
          'A short code and a name. The kind — market, region, firm — is a label for people reading the list; it does not change how limits are worked out.',
      },
      {
        what: 'Choose whose it is',
        detail:
          'A group belongs to one desk or to the whole firm. A desk’s group holds only that desk’s funds, and the desk sets its limits. The whole firm’s groups, such as a regulatory regime across every desk, are compliance’s. Somebody working on one desk makes that desk’s groups without choosing.',
      },
      {
        what: 'Put funds in it',
        detail:
          'On “View funds in a group”. You see the limits a fund is about to inherit before you commit. A desk’s group offers only that desk’s funds.',
      },
      {
        what: 'Take a limit off the group',
        detail:
          'Open the group’s limits and choose Propose to detach on the limit’s ⋯ menu, with the day and why. It ends the limit for every fund in the group, so an approver who did not propose it decides on Approvals.',
      },
    ],
    workflow:
      'Before a policy that covers several funds. One limit on a group beats the same limit typed onto thirty mandates, because the thirty cannot then drift apart.',
    roles:
      'An administrator creates groups and decides which funds are in them; a desk’s administrator does so for the desk’s own groups. An approver decides what limits a group carries — two different people, on purpose.',
    notes: [
      'Creating a group applies nothing. It is an empty set until an approver attaches a limit and funds are put in it.',
      'A fund in one of its desk’s groups leaves the group before it moves to another desk.',
    ],
  },
  {
    id: 'membership',
    title: 'View funds in a group',
    purpose:
      'Which portfolios belong to which scope — a market, a fund family, a firm-wide policy set.',
    before: ['A scope exists.', 'The portfolio exists.'],
    steps: [
      {
        what: 'Preview before you commit',
        detail:
          'Adding a portfolio to a scope makes every rule on that scope bind it. The rules it is about to inherit are listed first, and the list you were shown is what gets recorded.',
      },
      {
        what: 'Expect an attestation to be brought forward',
        detail:
          'A portfolio that just inherited a rule set has its next attestation pulled in, so somebody confirms the new position rather than discovering it at the next cycle.',
      },
    ],
    workflow:
      'Group membership changes for ordinary operational reasons — a fund is redomiciled, a desk reorganises — so an administrator handles it. What keeps it safe is that you see the limits first, the change is recorded, and the fund’s next sign-off is brought forward.',
    roles: 'An administrator decides which funds are in a group. An approver decides what limits the group carries — two different people, on purpose.',
  },
  {
    id: 'submissions',
    title: 'Guideline mailbox',
    purpose:
      'What arrived in the guideline mailbox: instructions that were refused and why, document shapes nobody has mapped yet, and the list of addresses allowed to submit.',
    before: ['The mailbox configured and polling turned on — see Guidelines Mailbox Setup.'],
    steps: [
      {
        what: 'Work the refusals',
        detail:
          'Each says what refused it and quotes the sentence that caused it. The useful next action is usually to call the client, which needs the wording rather than the word "rejected".',
      },
      {
        what: 'Map an unfamiliar shape once',
        detail:
          'A layout nobody has seen waits for a person rather than being guessed at. Once mapped, every later document in that shape is read with no further confirmation. A layout is saved only once at least one row has a meaning, and its row meanings can be changed later from its ⋯ menu under Layouts mapped.',
      },
      {
        what: 'Say when its guidelines take effect',
        detail:
          'Mapping a layout asks where the effective date comes from: a cell in the document, the day each document arrives, or a date you give for each document. With the last, each document waits under Waiting for a date until someone enters the day it takes effect, and is then read.',
      },
      {
        what: 'Keep the sender list current',
        detail:
          'Only addresses on it are considered, per portfolio. Revoked entries stay visible, because "who was allowed to send this when it arrived" is the question asked about an instruction accepted last year.',
      },
    ],
    workflow:
      'Everything arriving from outside. Anything that passes the checks becomes a proposal for an approver — nothing on this screen puts a client’s instruction into force.',
    roles:
      'Anyone signed in can read all three lists, because they matter to different people. Closing a refused item is for whoever handles client instructions; the sender list is the administrator’s.',
    notes: [
      'CSV and Excel attachments are read automatically. Any other attachment comes back to the queue with a note listing the formats to send.',
    ],
  },
  {
    id: 'connector',
    title: 'Guidelines Mailbox Setup',
    purpose: 'Point the appliance at a mailbox, prove it can read it, and turn it on.',
    before: [
      'An IMAP mailbox and its password.',
      'The name your own mail server writes in its Authentication-Results header.',
    ],
    steps: [
      { what: 'Fill in host, user and password, and save', detail: 'Saving does not start reading anything.' },
      {
        what: 'Test the connection',
        detail:
          'It reads the folder and reports, per message, the sender and whether it can be established as genuine. Nothing is written and nothing is marked read.',
      },
      {
        what: 'Set the trusted mail server',
        detail:
          'Expect every message to say NOT verified on the first run. The test lists the server names your mail actually carries — pick the one you recognise as your own mail server, save, and test again. It lists them rather than choosing for you, because a sender can put any name there.',
      },
      {
        what: 'Press Turn on',
        detail:
          'At the top of the screen. It takes effect at once, and the mailbox is then read every 5 minutes; Scheduled Jobs shows what each read came to. Read the mailbox now is for checking it works.',
      },
    ],
    workflow: 'Set up once. After that the mailbox feeds the Guideline mailbox queues and the Approvals screen.',
    roles: 'An administrator. Not the approver — whoever decides which mailbox is read should not also decide whether what arrives takes effect.',
    notes: [
      'Until a trusted server is set, messages wait in the queue rather than being accepted — the safe default for a mailbox that proposes changes to limits.',
      'The password is stored so this system can sign in to the mailbox. It is never shown again — leave the field blank to keep the one already saved.',
    ],
  },
  {
    id: 'users',
    title: 'People',
    purpose: 'Every account on this appliance, its role, and how it signs in.',
    before: ['Nothing. This is where onboarding starts.'],
    steps: [
      {
        what: 'Create the account and send the invitation',
        detail:
          'No password is set here and none is ever sent to you. The invitation is single-use, expires in three days, and is shown once — only its hash is stored.',
      },
      {
        what: 'Set the role',
        detail:
          'Changing a role ends that person’s live sessions, so a demotion takes effect immediately rather than whenever they next sign out.',
      },
      {
        what: 'Choose where they work',
        detail:
          'The whole firm, or their own desks. An administrator limited to desks adds those desks’ funds and groups and puts funds into their groups; people, the mailbox and the system stay with an administrator of the whole firm. Compliance works across the whole firm: every desk’s funds, the limits set for groups of funds, the dictionary, verdict reviews and the activity record. A portfolio manager’s team works on its desks and sees and changes only the funds those desks run, alongside every group limit that applies to them. Compliance officer fills in author and approver across the whole firm in one click. Change it later from Change desks in the ⋯ menu.',
      },
      {
        what: 'Deactivate when somebody leaves',
        detail:
          'They are signed out at once. The account stays, because the activity record has to keep naming who did what — and the people an auditor asks about are often the ones who have left.',
      },
    ],
    workflow: 'Before anything else. Every other screen depends on somebody holding the right role.',
    roles: 'Administrators look after accounts and roles. Granting access and putting limits into force are separate roles, so a firm can keep them with different people. In a small team one person can hold several roles, and does everything each of them allows, as the matrix below shows.',
    roleMatrix: true,
    notes: [
      'You can issue a fresh invitation for an account nobody has set up yet. Not for one in use — that would be a way to take somebody’s account over.',
      'People sign in through your firm’s identity provider where one is set up, under your firm’s own sign-in policy; otherwise with the password they chose on redeeming their invitation.',
    ],
  },
  {
    id: 'desks',
    title: 'Desks',
    purpose:
      'The firm’s investment teams: an EM debt desk, a multi-asset desk, a money-market desk. Every fund is run by one desk.',
    before: ['Nothing. The firm starts with one desk, Main desk, and every fund is on it until you add more.'],
    steps: [
      {
        what: 'Add a desk',
        detail: 'A code the firm already uses for the team, and its name.',
      },
      {
        what: 'Add its markets',
        detail:
          'Choose Add a market from the desk’s ⋯ menu. The market’s starter terms join the firm’s dictionary, alongside anything already defined, and the desk lists the markets it was set up with.',
      },
      {
        what: 'Put funds on it',
        detail:
          'Choose the desk when adding funds, or under View funds choose Move to another desk from a fund’s ⋯ menu.',
      },
      {
        what: 'Rename or delete one',
        detail:
          'Rename from the ⋯ menu at any time. Delete removes a desk that runs no funds; the firm always keeps at least one.',
      },
    ],
    workflow:
      'Before funds. Each fund is placed on a desk as it is added. Everyone sees the desks they work on; open a desk’s ⋯ menu for its funds and groups.',
    roles:
      'An administrator of the whole firm sets up desks and moves funds between them. A desk’s administrator adds the desk’s funds and groups.',
    notes: [
      'Every desk shares one dictionary, so firm-wide limits and comparisons across desks read the same way.',
      'Every move of a fund between desks is recorded in the audit trail.',
    ],
  },
  {
    id: 'funds',
    title: 'Funds',
    purpose:
      'The portfolios this system holds guidelines for. A fund has to exist before any limit can be recorded against it.',
    before: ['Nothing. This is usually the second thing you set up, after accounts.'],
    steps: [
      {
        what: 'Add one',
        detail:
          'Code, name and currency. The code is how the fund is identified in workbooks, exports and client mail, so use the one your firm already uses. Domicile and regime are optional.',
      },
      {
        what: 'Or add several at once',
        detail:
          'Paste rows straight from a spreadsheet — code, name, currency, and optionally domicile and regime. You see them listed before anything is created, and each is added separately so a bad row does not stop the rest.',
      },
      {
        what: 'Check the codes before you add them',
        detail:
          'A mistyped code becomes a fund nobody meant to create, and limits meant for the real one end up on it.',
      },
      {
        what: 'Set who owns it',
        detail:
          'Under View funds, choose Change owner from the fund’s ⋯ menu. The owner signs the fund off and answers for its cure obligations, so it is an author or an approver. A fund with no owner takes the author of its first limit put into force.',
      },
      {
        what: 'Choose its desk',
        detail:
          'Once the firm has more than one desk, choose the desk before adding funds; every fund you add goes on it. Under View funds, Move to another desk in the fund’s ⋯ menu changes it later.',
      },
      {
        what: 'Correct or delete one',
        detail:
          'Under View funds, choose Edit from the fund’s ⋯ menu to correct its name, domicile or regime at any time. Its code and currency are correctable too until limits, holdings or sign-offs are recorded against the fund; from then on they stay, so every record keeps naming the fund the same way. Delete removes a fund nothing is recorded against, such as one added twice.',
      },
    ],
    workflow:
      'Before guidelines. Importing a workbook matches its columns against funds that already exist here, so this comes first.',
    roles:
      'An administrator adds funds. Which funds the firm runs is an operational matter; what limits apply to them is the approver’s.',
    notes: [
      'Adding a fund applies no limits. It is an empty record until an approver approves a rule for it.',
      'Two funds cannot share a code, because the code is what identifies one everywhere else.',
    ],
  },
  {
    id: 'promotions',
    title: 'Repeated limits',
    purpose:
      'The same limit written against many funds, found across the whole store — so a firm-wide policy held as forty copies can become one rule on a group.',
    before: [
      'A group exists to promote the limit to.',
      'The document that authorises it for that group, which is not the same as the mandates the copies came from.',
    ],
    steps: [
      {
        what: 'Read the candidates',
        detail:
          'Each row is one limit that appears against several funds at the same threshold. It looks across every import, mailbox submission and fund added since — not just the one load you happen to be in.',
      },
      {
        what: 'Decide whether it is one policy or several coincidences',
        detail:
          'Twelve funds at 2% may be one house policy or twelve independent negotiations, and nothing in the data tells them apart. That is why this is called a candidate and why it promotes nothing on its own.',
      },
      {
        what: 'Propose it against a group and a document',
        detail:
          'Proposing binds nothing. An approver still decides, and is shown every fund the group reaches before they do.',
      },
    ],
    workflow:
      'Occasional, and usually afterwards — the moment somebody notices a policy has been copied is while reading, long after the import that copied it.',
    roles:
      'An author proposes; an approver decides. The screen is in their menus rather than an administrator’s, who looks after the groups a limit is promoted to. The copies themselves stay until the group rule is approved.',
    notes: [
      'What this creates is house policy, whatever the copies were. A contractual limit cannot attach to a group, because a contract binds a counterparty rather than a region.',
      'The per-fund copies are not removed by proposing. They come off once the group rule is in force, which is a separate act with its own record.',
    ],
  },
  {
    id: 'excel',
    title: 'Excel add-in',
    purpose:
      'Look up a fund from a cell in Excel: its limits in force, and with the Snapshot Evaluator its latest verdicts, in a panel beside the spreadsheet you are working in. The add-in reads the cell you select and writes nothing into your workbook.',
    before: [
      'A GUARD account.',
      'Excel on the web for yourself, or Excel for Windows, Mac and the web through your Microsoft 365 administrator.',
    ],
    steps: [
      {
        what: 'Download the add-in file',
        detail:
          'From Guidelines, Excel add-in. The file tells Excel where this installation is, and the screen shows the address the add-in will open.',
      },
      {
        what: 'Add it to Excel',
        detail:
          'For yourself, in Excel on the web: Home, Add-ins, More add-ins, My add-ins, Upload my add-in, then choose the file. For your firm, a Microsoft 365 administrator uploads the same file in the Microsoft 365 admin center under Settings, Integrated apps, Upload custom apps.',
      },
      {
        what: 'Sign in and choose a fund',
        detail:
          'Open GUARD guidelines from Home, Add-ins, and sign in: with Microsoft, which opens in a small window, or with your GUARD email and password. Select a cell holding a fund’s code, such as HALVEN, and choose Use selected cell.',
      },
      {
        what: 'Read its limits or verdicts',
        detail:
          'Guidelines shows the limits in force. With the Snapshot Evaluator, Verdicts shows the fund’s latest verdicts, each with the day its holdings were as at. Select another fund’s cell and choose Use selected cell again.',
      },
      {
        what: 'Review a verdict for compliance',
        detail:
          'With the Snapshot Evaluator, an approver reviews from the Verdicts tab: Review opens compliance’s verdict on the card, starting at GUARD’s, with why it differs and a note where it overrides GUARD. It is saved as a review in GUARD is, and counts on Compliance agreement.',
      },
    ],
    workflow:
      'Whenever a spreadsheet names funds: a position sheet, a trade blotter, a list of mandates.',
    roles: 'Everyone signed in can download the file and use the add-in, which shows what their role can read.',
    notes: [
      'Nothing is written into your workbook: a limit or a verdict copied into a cell stops being current the moment the rule is amended or the holdings move.',
      'The sign-in lasts while the panel is open and is never saved in the workbook, which people email to each other.',
      'The add-in talks to this installation and nothing else.',
    ],
  },
  {
    id: 'verdicts',
    title: 'Verdicts',
    pack: 'snapshot_evaluator',
    purpose:
      'Where every fund stands on its latest evaluation, on one page: in breach, with limits left undecided, within a warning of a limit, or inside every one, each judged on the holdings as at its snapshot’s date.',
    before: [
      'The Snapshot Evaluator pack on this installation.',
      'A snapshot of each fund, loaded under Load a snapshot.',
    ],
    steps: [
      {
        what: 'Read the totals',
        detail:
          'Funds are counted by their latest run: breach, not evaluable, warn, pass, and not evaluated. Not evaluable is counted as prominently as breach, because a limit that could not be judged has not been passed. Click a total to list those funds alone.',
      },
      {
        what: 'Open a fund’s verdicts',
        detail:
          'Click the fund, or choose Verdicts from its menu. Its verdicts open in place of the list: each limit with what it covers, what was found, the headroom, why, and the day the holdings were as at. The arrows step to the funds either side of it in the list, so after clicking a total you can read every fund in breach in turn. All funds returns to the list as you left it.',
      },
      {
        what: 'Evaluate where it is due',
        detail:
          'A fund says when holdings have been loaded since its run, or when its limits for that day have changed since. Evaluate from its menu, and the report shows the new run.',
      },
      {
        what: 'Look back, or export',
        detail:
          'Choose an as-at date to see the verdicts that stood at the end of that day. Choose Export, then CSV or Excel, for every fund or for the fund you filtered to: every verdict with its as-at date, and a sheet of the funds. Each export is recorded on the audit trail.',
      },
    ],
    workflow:
      'Daily, once the day’s holdings are loaded and evaluated. The report says which funds need attention; the fund’s verdicts say which limits and why.',
    roles: 'Everyone signed in reads the report, evaluates a fund from it, and exports it.',
    notes: [
      'Advisory. Pre-trade checks run in your order management system against the exported rules, and these verdicts show where each fund stood on the day its holdings were as at.',
      'Every fund is listed, including a fund not yet evaluated, so the report covers the whole firm.',
      'In Excel, the GUARD add-in has a Verdicts tab beside Guidelines. Select a cell holding a fund’s code and choose Use selected cell: the pane shows that fund’s latest verdicts, each with the day its holdings were as at, and writes nothing into the workbook.',
      'A fund whose limits are all written as words counts as not evaluable, since a verdict is given on a number or a rating.',
    ],
  },
  {
    id: 'agreement',
    title: 'Compliance agreement',
    pack: 'snapshot_evaluator',
    purpose:
      'Compliance reviews each of GUARD’s verdicts with the figures in front of it, agreeing or overriding with a note, and this dashboard shows how many were reviewed, agreed and overridden.',
    before: [
      'The Snapshot Evaluator pack on this installation.',
      'Funds evaluated, with verdicts to review.',
    ],
    steps: [
      {
        what: 'Open a fund’s verdicts',
        detail:
          'Under Dashboards, Verdicts, open the fund; or choose Review verdicts from a fund’s ⋯ menu here. Each verdict shows where it stands with compliance: awaiting review, agreed, overridden, or changed since review.',
      },
      {
        what: 'Review a verdict',
        detail:
          'Choose Compliance review from the verdict’s ⋯ menu. GUARD’s verdict is laid out in full: what the limit says and covers, what was found, the headroom, why, each issuer or country with its figure, the cell it came from and the holdings file judged.',
      },
      {
        what: 'Take the final call',
        detail:
          'Choose compliance’s verdict: Within the limit, Breach or Not evaluable. It starts at GUARD’s. Where it differs from a verdict GUARD decided, say why (the limit is recorded wrong, the holdings data is missing or wrong, compliance reads the limit differently, or an accepted difference) and add a note. Save, or Save and next to step to the fund’s next verdict.',
      },
      {
        what: 'Read the agreement',
        detail:
          'The figure is the share of reviewed verdicts where compliance agrees with GUARD. A limit GUARD left not evaluable that compliance decided counts against it. Limits written as words, and limits against a benchmark without its figures, are read by compliance and counted apart. Overridden lists every override with its cause and note, counted by cause.',
      },
    ],
    workflow:
      'Before relying on a fund’s verdicts: every fund with a day on which compliance reviewed every verdict. The figure then travels with the verdicts, on the Verdicts screen, in its export and in the Excel pane.',
    roles:
      'Everyone who reads verdicts reads compliance’s reviews and this dashboard. Approvers review verdicts, and see how many await review in My pending actions.',
    notes: [
      'A review never changes GUARD’s verdict, breach counts or cure obligations. It is recorded beside the verdict.',
      'A later evaluation of the same day that comes to the same verdict keeps its review; one that differs is marked changed since review, to review again.',
      'A revised review keeps the one before it, listed under Reviews.',
    ],
  },
  {
    id: 'dictionary-reconcile',
    title: 'Reconcile with holdings',
    pack: 'snapshot_evaluator',
    purpose:
      'Where the classification codes your holdings carry, the Dictionary’s members and the codes your limits name disagree, for a person to decide.',
    before: [
      'The Snapshot Evaluator pack on this installation.',
      'Holdings snapshots whose files map classification columns, such as issuer group, country or sector.',
    ],
    steps: [
      {
        what: 'Read what disagrees',
        detail:
          'Across each fund’s newest holdings, three kinds of finding. Named by a limit, not held: a limit names a code no holding carries, so it measures nothing; the codes held nearest to it are shown. Held, not in the Dictionary: holdings carry a code the Dictionary does not define, so no limit naming that classification matches them. Entered by hand, not held: a member somebody typed or imported that no holding carries.',
      },
      {
        what: 'Request a held code as a member',
        detail:
          'From the finding’s ⋯ menu, give the member its name. The request waits under Term requests for an approver, like any other.',
      },
      {
        what: 'Dismiss what needs nothing done',
        detail:
          'From the finding’s ⋯ menu, say why: a custodian’s cash line is not an issuer group; the funds simply hold no bond in that sector. A dismissed finding is listed below, with its note, and Reopen puts it back.',
      },
      {
        what: 'Correct a limit naming the wrong code',
        detail:
          'Amend the limit so it names the code the holdings carry, through the usual proposal and approval. The finding clears once no limit names the code nobody holds.',
      },
    ],
    workflow: 'After holdings from a new custodian or a new classification column first load, and when a limit reads as within its limit for no reason you can see.',
    roles:
      'Everyone who can read the Dictionary reads the findings. Authors and approvers request a member. Approvers dismiss and reopen findings, and see how many are open in My pending actions.',
    notes: [
      'Nothing in the Dictionary or in a limit changes on its own: each finding is a person’s decision.',
      'Shipped members, such as every country code, are not listed as entered by hand.',
    ],
  },
  {
    id: 'whatif',
    title: 'What-if trades',
    pack: 'snapshot_evaluator',
    purpose:
      'Candidate buys and sells applied to a fund’s newest holdings, and the fund judged before and after them, so you see which limits a trade idea would move before it goes to the desk.',
    before: ['The Snapshot Evaluator pack on this installation.', 'A snapshot of the fund, loaded under Load a snapshot.'],
    steps: [
      {
        what: '1. Fund',
        detail:
          'Choose the fund. Its newest holdings are summarised: the day they were as at, the positions, the NAV, the cash trades settle in, where its latest verdicts stand, and its largest holdings. Choose Next: trades.',
      },
      {
        what: '2. Trades',
        detail:
          'Buy or Sell, the security and the amount in the fund’s base currency. A security the fund holds is suggested as you type. For one it does not hold, give what you know of it, such as its issuer, country, rating and maturity; anything left blank counts as unknown, never as within a limit. Remove a trade from its ⋯ menu, or Clear to start again, then choose Evaluate.',
      },
      {
        what: '3. Results',
        detail:
          'The counts show how many limits are in each state before and after, then the trades as applied with the cash, then every limit the trades change: what was found before and after, the room left, and why. Show every limit lists the rest too. Change trades goes back to the trades; changing one clears the results.',
      },
      {
        what: 'From Excel',
        detail:
          'In the GUARD add-in, open What-if, select the rows of trades on a sheet whose header row names the fund, the side, the security and the amount, and choose Check selected trades. Each fund’s trades are applied to its newest holdings.',
      },
    ],
    workflow: 'Before a trade idea goes to the desk. Pre-trade checks run in your OMS against the exported rules.',
    roles: 'Everyone signed in runs a what-if. Each one is on the audit trail, with its trades.',
    notes: [
      'Advisory. Nothing is traded, and the holdings stay as they were loaded.',
      'A sale of more than the fund holds, or of a security it does not hold, is refused, and every such trade is named at once.',
      'Buys are paid from the fund’s cash line in its base currency and sales paid into it, so a cash limit sees the trade. The NAV stays as the snapshot states it.',
    ],
  },
  {
    id: 'cure',
    title: 'Cure obligations',
    pack: 'snapshot_evaluator',
    purpose:
      'Every holding that has fallen below a fund’s rating floor, the time the fund’s sell-by period gives to sell it, counted to the last settlement day on the fund’s calendar, and how each one ended.',
    before: [
      'The Snapshot Evaluator pack on this installation.',
      'A rating floor and a sell-by period among the fund’s limits, such as Minimum rating B- and Process for selling bond that falls below Min Rating: 6 months.',
      'Snapshots of the fund with ratings, loaded day by day, so the day a bond fell below its floor can be read from them.',
    ],
    steps: [
      {
        what: 'See what is running',
        detail:
          'Obligations still running come first, soonest deadline first. The totals count escalating, open, breached, remediated and waived; click one to list those alone. An obligation opens by itself when a fund’s newest holdings are evaluated and a bond has fallen below its floor within the fund’s sell-by period.',
      },
      {
        what: 'Read one',
        detail:
          'Click it. Details lists the fund, the limit, the holding, the day it fell below, the period, the deadline and the calendar it is counted on, and the fund’s owner. History lists everything that has happened to it.',
      },
      {
        what: 'Record the sale',
        detail:
          'Choose Record remediation and give the trade reference. A later snapshot inside the window that no longer holds the bond below its floor closes the obligation as remediated too, naming that snapshot’s day.',
      },
      {
        what: 'Record the client’s consent',
        detail:
          'Where the client agreed to the bond being kept, register their letter under Mandate documents, then choose Record waiver and cite it.',
      },
      {
        what: 'Open one by hand',
        detail:
          'For an event the holdings do not show yet, such as an agency’s announcement, choose Open an obligation: the fund, the limit, the holding, the day, and what happened. The fund’s own sell-by period applies unless you give another, such as P30D.',
      },
    ],
    workflow:
      'Each morning, once the day’s holdings are evaluated. Escalating obligations come first; a breached one stays on the list with its whole history.',
    roles:
      'Everyone signed in reads obligations. Authors and approvers record a remediation and open an obligation by hand. Approvers record a waiver.',
    notes: [
      'Obligations escalate at the days before the deadline set under Settlement calendars, and wait in My pending actions for authors and approvers.',
      'The day after its deadline an obligation still running is breached. A breached, remediated or waived obligation is kept as it ended, and the audit pack lists every obligation raised in its period.',
      'In Scheduled Jobs, the daily cure obligations run names each one it escalated or breached, and opens it.',
    ],
  },
  {
    id: 'benchmarks',
    title: 'Benchmarks',
    pack: 'snapshot_evaluator',
    purpose:
      'The figures a limit set against the benchmark is judged on: the duration and the weights by country, currency or bucket, by day, of each benchmark your funds’ guidelines name.',
    before: [
      'The Snapshot Evaluator pack on this installation.',
      'Guidelines imported with each fund’s benchmark, which adds it here.',
      'Figures your firm exports from its index provider, under its own licence.',
    ],
    steps: [
      {
        what: 'See the benchmarks',
        detail:
          'Under Benchmarks, on the left, each benchmark a fund’s guidelines name, with the funds naming it and the latest day its figures are loaded for. A composite such as 50/50 EUR Hedged is a benchmark with figures of its own.',
      },
      {
        what: 'Load the figures',
        detail:
          'Under Figures, take the template: a row for each figure the funds’ limits need, for the latest holdings day. Fill in Value from your index provider’s export, add a row for each other member the benchmark holds, and load it. Duration is in years; a weight is the percentage of the benchmark in that member, and a classification’s weights add up to 100. Where a limit holds investment grade and high yield to the benchmark apart, those weights carry a Rating band, and across both bands they add up to 100. Every row is checked first, and a row that does not read is named. A later file for a benchmark and day replaces that day’s figures.',
      },
      {
        what: 'Evaluate',
        detail:
          'A limit such as BM +/- 1 year, BM +/- 30% of the benchmark’s duration, or BM + 15% for each country, is judged against the benchmark’s figures as at the holdings’ day, or the latest in the 31 days before it. The verdict names the benchmark, its day and its figure, and each country is judged against its own weight in the benchmark. No off-benchmark countries holds every holding, cash aside, to the countries the benchmark carries weight in.',
      },
    ],
    roles:
      'Everyone who reads verdicts sees the benchmarks and takes the template. Authors and approvers load figures.',
    notes: [
      'A benchmark in the file is written as the guidelines name it; a name no fund’s guidelines use is named back, row by row.',
      'Figures stay as loaded, and an evaluation reads those loaded by the time it read its limits, so evaluating again gives the same answer.',
      'Figures files can arrive on their own: name a Benchmark figures folder on SFTP - Auto loading snapshots, and each file your index provider puts there loads within 15 minutes.',
    ],
  },
  {
    id: 'calendars',
    title: 'Settlement calendars',
    pack: 'snapshot_evaluator',
    purpose:
      'The calendars cure obligation deadlines fall on, the calendar each fund uses, and when obligations escalate.',
    steps: [
      {
        what: 'Put funds on calendars',
        detail:
          'Under Funds, on the left, choose a fund’s calendar, or leave it on the firm’s default. Obligations already running keep the calendar they opened on.',
      },
      {
        what: 'Add a calendar',
        detail:
          'Under Calendars, choose Add a calendar: a code such as TARGET2, a name and its weekend. Mark it the firm’s default to use it for every fund without a calendar of its own.',
      },
      {
        what: 'Add its holidays',
        detail:
          'Choose Holidays from its menu, then paste them or read a file, one a line: 2026-12-25, Christmas Day. A line that does not read refuses the list, each such line named. An obligation whose deadline now falls on a holiday moves to the settlement day before it.',
      },
      {
        what: 'Set escalation',
        detail:
          'Under Escalation, up to three numbers of days before the deadline, such as 14, 3. Each reached escalates an obligation once more.',
      },
    ],
    roles: 'Approvers set calendars, the funds on them, and escalation.',
    notes: [
      'A deadline is the last settlement day on or before the end of the fund’s sell-by period.',
      'A removed holiday stays on record, and no deadline already set moves later for it.',
    ],
  },
  {
    id: 'holdings',
    title: 'Holdings snapshots',
    pack: 'snapshot_evaluator',
    purpose:
      'The holdings a verdict is judged against: a file from your order management system, custodian or fund administrator, read through a column mapping you confirm, kept exactly as loaded, and dated with the day it was as at.',
    before: [
      'The Snapshot Evaluator pack on this installation.',
      'The funds in the file, set up under Funds & groups.',
      'For classification or rating columns, the scheme or the rating scale in the Dictionary.',
    ],
    steps: [
      {
        what: 'Choose the file',
        detail:
          'CSV or Excel. The header row is found for you, below any title block, and every column gets a suggestion from its name, or the saved mapping if a file laid out like this has been loaded before.',
      },
      {
        what: 'Say which fund, and when',
        detail:
          'Where the file’s name carries a fund code and a date, as HALVEN_2026-09-11.csv does, both are filled in for you to check. Otherwise choose the fund, or map a fund column and one file becomes a snapshot for every fund it holds. The as-of date is the one every verdict from the snapshot carries. NAV and net assets come from the file or are typed in.',
      },
      {
        what: 'Check what each column holds',
        detail:
          'Sample values from the snapshot sit beside each column, so a market value mapped to the wrong column is plain to see. Security identifier and market value are needed; every other column is used by the limits that need it. Dates written like 03/04/2026 are read the way the file’s own dates prove: one such as 15/08/2036 can only be day first. You are asked only when no date in the file decides it.',
      },
      {
        what: 'Load it',
        detail:
          'The file loads whole. Anything that stops it, such as a number written 1.234,56 or a position count the rows do not match, is listed by row, all at once, so the export is corrected in one pass.',
      },
    ],
    workflow:
      'The input to evaluation. A corrected file loads as a new snapshot beside the first, because a verdict names the snapshot it was judged against.',
    roles: 'Authors and approvers load snapshots and save mappings. Everyone signed in can read them.',
    notes: [
      'Open a snapshot to read its positions a page at a time, find a security or an issuer across the whole book, and export it as CSV or Excel. Each export is recorded on the audit trail.',
      'Save the mapping when you load a new layout. The next file with the same columns arrives mapped, and a changed mapping saved again becomes its new version.',
      'A file whose dates can be read both ways, such as 25/08/2033 in one row and 08/25/2034 in another, is refused rather than guessed at. So is a saved mapping whose date order the file’s own dates contradict, which is how a changed export is caught.',
      'Classification and rating values the Dictionary does not define load as the file states them, and the snapshot lists them.',
    ],
  },
  {
    id: 'holdings-feed',
    title: 'SFTP - Auto loading snapshots',
    pack: 'snapshot_evaluator',
    purpose:
      'Holdings files collected from your custodian’s or fund administrator’s SFTP or FTPS server every 15 minutes, loaded with the mapping saved for their layout, and evaluated as they arrive.',
    before: [
      'The server’s address, a user, and a password or private key, from your provider.',
      'For SFTP, the server’s key fingerprint, from your provider, to compare with the one it presents.',
      'One file of each layout loaded by hand under Load a snapshot, with its mapping saved.',
    ],
    steps: [
      { what: 'Fill in the server and save', detail: 'Saving reads nothing. The password or private key is never shown again; leave it blank to keep the one saved.' },
      {
        what: 'Test the connection',
        detail:
          'It shows the key the server presented and the files waiting in the folder. Compare the key with the one your provider gave you, and press Trust this key when they match.',
      },
      { what: 'Press Turn on', detail: 'At the top of the screen. It takes effect at once, and Scheduled Jobs shows each pass, file by file.' },
    ],
    workflow:
      'Each new file loads as a snapshot, exactly as one loaded by hand would, and the funds it holds are evaluated straight after. Scheduled Jobs shows what each file came to.',
    roles: 'An administrator sets it up and trusts the server’s key. Everyone signed in reads the snapshots it loads.',
    notes: [
      'A file in a layout nobody has mapped waits, and its pass in Scheduled Jobs says so. Load one file of that layout by hand and save its mapping; the waiting file loads on the next pass.',
      'The fund and the day come from the file’s columns, or from its name, as in HALVEN_2026-09-12.csv.',
      'A refused file is listed in its pass in Scheduled Jobs with every reason. Put a corrected file up under the same name and it is collected again, or press Try again there once the cause is fixed on this side, such as a fund now set up.',
      'A server presenting a key other than the trusted one is not read from, and the pass says which key it presented. Check with your provider before trusting a new key.',
      'Over FTP over TLS, a certificate valid for the host name you entered needs nothing trusted. One issued for another name, as on shared hosting, is trusted the same way as a key, or you can connect by a name it is valid for. Plain FTP is not offered, as it sends the password unencrypted.',
      'Where Move loaded files to names a folder, a file is moved there once its snapshot is saved. Left empty, files stay and each is read once.',
      'Name a Benchmark figures folder and each figures file put there loads as one loaded under Benchmarks would, every row checked first, and is listed in its pass beside the holdings files. A fund judged against its benchmark is evaluated again when figures for its day arrive after its run.',
    ],
  },
  {
    id: 'job-history',
    title: 'Scheduled Jobs',
    purpose:
      'Every run of the scheduled jobs in the last month: when it ran, what it came to, and what it produced.',
    steps: [
      {
        what: 'Narrow the list',
        detail: 'By job, by state, and by the days a run was queued. The filters reach every run kept, not only those on screen.',
      },
      {
        what: 'Open what a run produced',
        detail:
          'Choose Details from the run’s ⋯ menu. It shows when the run was queued, started and finished, who asked for it, and why it failed if it did, then lists what it produced fund by fund. Each row’s ⋯ menu opens it: the snapshot a pass of automatic loading loaded and its verdicts, the verdicts monitoring made, or the document a client sent.',
      },
      {
        what: 'Read a pass of automatic loading',
        detail:
          'Its details list every file it collected: loaded, with its snapshots; already loaded; refused, with every reason; or waiting for a mapping. Each file’s ⋯ menu opens its snapshot, goes to Load a snapshot to map a layout that is waiting, or, for an administrator, has a refused or waiting file read again in a run started straight away, which the file then points to. Saving a mapping also starts a run for the files waiting on one.',
      },
    ],
    workflow: 'The record of what the appliance did on its own. The schedule itself is under Settings → System → Jobs.',
    roles: 'Everyone signed in reads it. An administrator changes the schedule and has a file read again.',
    notes: ['Runs are kept for a month. What a run changed stays on the audit trail for as long as that is kept.'],
  },
  {
    id: 'audit',
    title: 'Audit trail',
    purpose:
      'Every act this appliance recorded, newest first — who did it, when, to what, and what the values were before and after.',
    before: ['Nothing. The record has been kept since the appliance was installed.'],
    steps: [
      {
        what: 'Narrow to the question you arrived with',
        detail:
          'By kind of act, by person, or by dates. The list of acts is read off the record itself, so it offers what this appliance has actually done rather than a fixed list that drifts.',
      },
      {
        what: 'Open a row to see what changed',
        detail:
          'Fields, with the value before and after. A field that did not move is still listed, because a change shown on its own cannot say what it was a change to.',
      },
      {
        what: 'Page back through time',
        detail:
          'Older and Newer move a page at a time. The record only grows at the newest end, so paging is anchored to entries rather than to positions — nothing is seen twice or skipped when something arrives while you are reading.',
      },
    ],
    workflow:
      'When somebody asks a question about the past. Who turned that setting off, who removed that group, whose sign-in kept failing.',
    roles:
      'An administrator or an approver. It shows every act on every fund, which is more than an author needs to do their work.',
    notes: [
      'Nothing on this screen changes anything, including the record. That is the point of keeping one.',
      'Acts with nobody behind them are the appliance itself — a nightly seal, a mailbox poll — and are shown as such rather than left blank.',
      'Each day is locked with a digest, and the audit pack carries it. That is what makes a claim about this record checkable rather than merely asserted.',
      'This is not the audit pack. The pack is a dossier assembled for somebody outside the firm, per fund; this is the supervisory read of what happened here.',
    ],
  },
  {
    id: 'operations',
    title: 'System',
    purpose:
      'The housekeeping that keeps this installation healthy: the audit chain, the scheduled jobs, document drift, and the support bundle.',
    before: ['Nothing.'],
    steps: [
      {
        what: 'Check the audit trail',
        detail:
          'Two things matter: that nothing has been altered since it was locked, and that locking is up to date. A record that stopped being locked a month ago is not a healthy one, however intact the rest is.',
      },
      {
        what: 'Check the scheduled jobs',
        detail:
          'Jobs lists what runs on its own: sealing the audit trail each night, reading the guideline mailbox every five minutes once it is on, and with the Snapshot Evaluator, evaluating each fund whose holdings or limits have changed. It shows what each last came to, when it runs next, and which worker is running the schedule. Each job’s ⋯ menu disables or enables it, changes when it runs, and runs it now. What each run came to is in Scheduled Jobs.',
      },
      {
        what: 'Produce a support bundle when we ask for one',
        detail:
          'It lists what it contains before producing anything. No limits, no document text and no passwords — log entries are included without the values that changed, so none of your thresholds or clause references leave with it.',
      },
      {
        what: 'See which add-on packs this installation runs',
        detail:
          'Add-ons, under System, shows each pack and whether this installation runs it. Packs are enabled under the installation’s licence.',
      },
    ],
    workflow:
      'Occasional. The scheduled jobs run on their own in the worker. Removing what the retention policy no longer keeps is done on the server, after a backup.',
    roles: 'An administrator. None of it can change what a limit says.',
    notes: [
      'Two things are done on the server. Upgrading the database follows a backup taken with your own tooling, and the very first account is created there, since it is what makes signing in possible.',
    ],
  },
]

export function helpFor(id: string): HelpTopic | undefined {
  // The menu promotes a screen's sections to entries of their own — `Term
  // requests` is part of the dictionary, `Who may send` part of the mailbox —
  // and they share their parent's help rather than each needing a page that
  // repeats it.
  const parent = id.split('-')[0]!
  return HELP.find((topic) => topic.id === id) ?? HELP.find((topic) => topic.id === parent)
}
